/**
 * Exportação de CSV — implementação compartilhada (CLAUDE.md §4).
 *
 * Antes cada tela reimplementava o mesmo par escape+download inline
 * (`pages/operacoes`, `pages/Payments`, `components/campaigns/legacy`), cada uma
 * com um detalhe diferente e um bug diferente: uma citava TODO campo sem
 * escapar aspas, outra usava `,` (que colide com o decimal pt-BR), outra
 * esquecia de revogar a object URL. Aqui a regra é uma só.
 *
 * Decisões que não são óbvias:
 *  - **Separador `;`**: em pt-BR o decimal é vírgula, então `,` como delimitador
 *    parte "R$ 1.234,56" em duas colunas. `;` é o que o Excel pt-BR espera.
 *  - **Linha `sep=;`**: dica proprietária do Excel — sem ela ele ainda tenta
 *    adivinhar pelo locale da MÁQUINA e erra em quem roda Excel em inglês.
 *  - **BOM UTF-8**: sem os 3 bytes EF BB BF o Excel lê como ANSI e "Anúncio"
 *    vira "AnÃºncio".
 *  - **CRLF**: fim de linha do RFC 4180 (e o que o Excel escreve).
 */

/** Separador padrão: ver comentário do módulo (decimal pt-BR usa vírgula). */
const DEFAULT_SEPARATOR = ';';
const CRLF = '\r\n';
const UTF8_BOM = new Uint8Array([0xef, 0xbb, 0xbf]);

/**
 * Escape RFC 4180: só cita quando precisa (separador, aspas, CR ou LF dentro do
 * valor) e dobra as aspas internas. Citar tudo "por garantia" — como faziam duas
 * das cópias antigas — engorda o arquivo e ainda assim quebra se o valor tiver
 * aspas, que é justamente o caso que o quote precisa resolver.
 */
export function EscapeCsvValue(value, separator = DEFAULT_SEPARATOR) {
    if (value === null || value === undefined) {
        return '';
    }
    const text = String(value);
    if (!text.includes(separator) && !text.includes('"') && !text.includes('\n') && !text.includes('\r')) {
        return text;
    }
    return `"${text.replace(/"/g, '""')}"`;
}

/**
 * Monta o texto do CSV.
 *
 * @param {Array<object>} rows
 * @param {Array<{label: string, getValue: (row) => unknown}>} columns
 *        `getValue` devolve o valor JÁ no formato que deve aparecer na planilha
 *        (o util não formata: quem sabe o que a coluna significa é o chamador).
 */
export function BuildCsv(rows, columns, { separator = DEFAULT_SEPARATOR, sepHint = true } = {}) {
    const safeColumns = Array.isArray(columns) ? columns : [];
    const safeRows = Array.isArray(rows) ? rows : [];

    const lines = [
        safeColumns.map((column) => EscapeCsvValue(column.label, separator)).join(separator),
        ...safeRows.map((row) =>
            safeColumns.map((column) => EscapeCsvValue(column.getValue(row), separator)).join(separator),
        ),
    ];

    if (sepHint) {
        lines.unshift(`sep=${separator}`);
    }
    return lines.join(CRLF);
}

/**
 * Monta e dispara o download. `fileName` entra sem extensão — o `.csv` é
 * acrescentado aqui para não haver arquivo "x.csv.csv".
 */
export function DownloadCsv({ rows, columns, fileName, separator = DEFAULT_SEPARATOR }) {
    const csv = BuildCsv(rows, columns, { separator });
    const blob = new Blob([UTF8_BOM, csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = `${fileName}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    // Sem o revoke o blob fica preso na memória da aba até o reload.
    URL.revokeObjectURL(url);
}
